
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { type ReactNode } from 'react';

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
  rightAction?: ReactNode;
}

const tabs = [
  { path: '/admin/dashboard', icon: 'ri-dashboard-line', activeIcon: 'ri-dashboard-fill', label: '總覽' },
  { path: '/admin/applications', icon: 'ri-file-list-3-line', activeIcon: 'ri-file-list-3-fill', label: '申請' },
  { path: '/admin/videos', icon: 'ri-video-line', activeIcon: 'ri-video-fill', label: '影片' },
  { path: '/admin/creators', icon: 'ri-team-line', activeIcon: 'ri-team-fill', label: '創作者' },
];

export default function AdminLayout({ children, title, rightAction }: AdminLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Desktop Header - 電腦版頂部導航 */}
      <header className="hidden md:block bg-slate-900/50 border-b border-slate-800 sticky top-0 z-40 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
                alt="Logo"
                className="h-10"
              />
            </Link>
            <nav className="flex items-center gap-1">
              {tabs.map((tab) => (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isActive(tab.path)
                      ? 'text-white bg-slate-800'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <i className={`${isActive(tab.path) ? tab.activeIcon : tab.icon} mr-2`}></i>
                  {tab.label === '總覽' ? '總覽' : tab.label === '申請' ? '申請管理' : tab.label === '影片' ? '影片審核' : '創作者管理'}
                </Link>
              ))}
            </nav>
          </div>
          <button
            onClick={handleSignOut}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line mr-2"></i>
            登出
          </button>
        </div>
      </header>

      {/* Mobile Header - 手機版頂部 */}
      <header className="md:hidden bg-slate-900 sticky top-0 z-40 px-4 py-3 flex items-center justify-between border-b border-slate-800/60">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/43e5ec71-732c-47e6-8652-53c813fd533d.png"
            alt="Logo"
            className="h-7"
          />
        </Link>
        <h1 className="text-white font-semibold text-base absolute left-1/2 -translate-x-1/2">{title}</h1>
        <div className="flex items-center gap-2">
          {rightAction}
          <button
            onClick={handleSignOut}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-800 hover:bg-slate-700 transition-colors cursor-pointer"
          >
            <i className="ri-logout-box-r-line text-sm text-slate-400"></i>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pb-20 md:pb-0 overflow-y-auto">
        {children}
      </main>

      {/* Mobile Bottom Tab Bar - 只在手機版顯示 */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-800/60 px-2 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around">
          {tabs.map((tab) => {
            const active = isActive(tab.path);
            return (
              <Link
                key={tab.path}
                to={tab.path}
                className={`flex flex-col items-center py-2 px-3 min-w-[64px] cursor-pointer transition-colors ${
                  active ? 'text-teal-400' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <div className={`w-6 h-6 flex items-center justify-center mb-0.5 ${active ? 'scale-110' : ''} transition-transform`}>
                  <i className={`${active ? tab.activeIcon : tab.icon} text-xl`}></i>
                </div>
                <span className={`text-[10px] font-medium ${active ? 'text-teal-400' : ''}`}>{tab.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
